/* SlotStatBar — premium slot-machine hero stat bar with a real physical lever
   - Vertical reel with motion blur, spring landing, accent flash
   - Clickable chrome+brass lever pulls down in an arc, then springs back
   - Web Audio API: mechanical clunk on lever pull, clicks during spin, chime on land
   - Sound toggle (localStorage persisted)
   - Respects prefers-reduced-motion
   - Hardware-accelerated transforms
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShieldCheck, Volume2, VolumeX } from "lucide-react";

/* ------------------------- audio engine ------------------------- */
class SlotAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.enabled = false;
    this.unlocked = false;
  }
  async unlock() {
    if (this.unlocked) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.18;
      this.master.connect(this.ctx.destination);
      if (this.ctx.state === "suspended") await this.ctx.resume();
      this.unlocked = true;
    } catch (e) { /* no audio available */ }
  }
  setEnabled(on) { this.enabled = !!on; }
  /**
   * Snap master gain to 0 when off-screen so any already-scheduled sounds
   * (spin clicks, landing chime) go silent instantly. Ramp back up on return.
   */
  setAudible(on) {
    if (!this.master || !this.ctx) return;
    try {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setTargetAtTime(on ? 0.18 : 0, t, 0.015);
    } catch {}
  }
  _canPlay() { return this.enabled && this.unlocked && this.ctx; }
  click(freq = 780, dur = 0.028) {
    if (!this._canPlay()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.65, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.35, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.master);
    osc.start(t); osc.stop(t + dur + 0.02);
  }
  /** Mechanical KA-CHUNK when the lever hits the bottom of its arc. */
  leverPull() {
    if (!this._canPlay()) return;
    const t = this.ctx.currentTime;
    // Sub-bass thud
    const thud = this.ctx.createOscillator();
    const tg = this.ctx.createGain();
    thud.type = "sine";
    thud.frequency.setValueAtTime(90, t);
    thud.frequency.exponentialRampToValueAtTime(45, t + 0.14);
    tg.gain.setValueAtTime(0.0001, t);
    tg.gain.exponentialRampToValueAtTime(0.45, t + 0.008);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    thud.connect(tg).connect(this.master);
    thud.start(t); thud.stop(t + 0.2);
    // Metallic contact click
    const clk = this.ctx.createOscillator();
    const cg = this.ctx.createGain();
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 1400;
    clk.type = "square";
    clk.frequency.setValueAtTime(1800, t + 0.005);
    clk.frequency.exponentialRampToValueAtTime(900, t + 0.06);
    cg.gain.setValueAtTime(0.0001, t + 0.005);
    cg.gain.exponentialRampToValueAtTime(0.22, t + 0.012);
    cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    clk.connect(hp).connect(cg).connect(this.master);
    clk.start(t + 0.005); clk.stop(t + 0.11);
  }
  startup() {
    if (!this._canPlay()) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(680, t + 0.18);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 900; bp.Q.value = 0.9;
    o.connect(bp).connect(g).connect(this.master);
    o.start(t); o.stop(t + 0.26);
  }
  landing() {
    if (!this._canPlay()) return;
    const t = this.ctx.currentTime;
    const chunk = this.ctx.createOscillator();
    const cg = this.ctx.createGain();
    chunk.type = "square";
    chunk.frequency.setValueAtTime(190, t);
    chunk.frequency.exponentialRampToValueAtTime(120, t + 0.05);
    cg.gain.setValueAtTime(0.0001, t);
    cg.gain.exponentialRampToValueAtTime(0.5, t + 0.005);
    cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    chunk.connect(cg).connect(this.master);
    chunk.start(t); chunk.stop(t + 0.11);
    [{ f: 880, d: 0.35, delay: 0.06 }, { f: 1320, d: 0.4, delay: 0.09 }].forEach(({ f, d, delay }) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f, t + delay);
      g.gain.setValueAtTime(0.0001, t + delay);
      g.gain.exponentialRampToValueAtTime(0.14, t + delay + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + delay + d);
      o.connect(g).connect(this.master);
      o.start(t + delay); o.stop(t + delay + d + 0.02);
    });
  }
}

/* ------------------------- component ------------------------- */
const ROW_H = 32; // px per row — must match CSS below

export default function SlotStatBar({ items, intervalMs = 5200, testid = "slot-stat-bar" }) {
  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches,
    []
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [phase, setPhase] = useState("active"); // idle | spinning | landing | active
  const [flash, setFlash] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [blurAmt, setBlurAmt] = useState(0);
  const [leverPulled, setLeverPulled] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  // Drag interaction: null = idle (CSS class rules apply); number = current inline rotation in deg
  const [dragAngle, setDragAngle] = useState(null);
  const [dragEasing, setDragEasing] = useState("none"); // css transition value while a drag/release is animating
  const [hitDots, setHitDots] = useState(0); // 0..5 — visible progress toward the easter egg
  const [easterBurst, setEasterBurst] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("cq_slot_sound") === "1";
  });

  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const timeoutsRef = useRef([]);
  const nextIdxRef = useRef(0);
  const runningRef = useRef(false);
  const dragRef = useRef({ active: false, startY: 0, moved: false, pointerId: null });
  const hitRef = useRef({ count: 0, lastAt: 0, resetTimer: null });

  const REPEATS = 6;
  const strip = useMemo(() => {
    if (!items?.length) return [];
    const out = [];
    for (let r = 0; r < REPEATS; r++) items.forEach((it, i) => out.push({ ...it, key: `${r}-${i}` }));
    return out;
  }, [items]);

  useEffect(() => {
    audioRef.current = new SlotAudio();
    audioRef.current.setEnabled(soundOn);
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.setEnabled(soundOn);
  }, [soundOn]);

  const scheduleTimeout = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timeoutsRef.current.push(id);
    return id;
  };

  const stopAll = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };

  const spinTo = (targetIdx) => {
    if (!items?.length) return;
    stopAll();

    if (reduced) {
      setSelectedIndex(targetIdx);
      setPhase("active");
      setFlash(true);
      scheduleTimeout(() => setFlash(false), 350);
      runningRef.current = false;
      return;
    }

    setPhase("spinning");
    audioRef.current?.startup();

    const finalRow = (REPEATS - 1) * items.length + targetIdx;
    const startY = -selectedIndex * ROW_H;
    const endY = -finalRow * ROW_H;
    const overshootY = endY - ROW_H * 0.35;

    const duration = 1900 + Math.random() * 350;
    const t0 = performance.now();

    let lastRow = Math.round(-startY / ROW_H);

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = easeOutCubic(p);
      let y;
      if (p < 0.88) {
        y = startY + (overshootY - startY) * (eased / easeOutCubic(0.88));
      } else {
        const sp = (p - 0.88) / 0.12;
        const springed = 1 - Math.pow(1 - sp, 2);
        y = overshootY + (endY - overshootY) * springed;
      }
      setTranslateY(y);
      const blur = Math.max(0, (1 - eased) * 1.4);
      setBlurAmt(blur);

      const row = Math.round(-y / ROW_H);
      if (row !== lastRow) {
        const speedFactor = 1 - eased;
        const freq = 620 + speedFactor * 340;
        audioRef.current?.click(freq, 0.022 + (1 - speedFactor) * 0.012);
        lastRow = row;
      }

      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setTranslateY(endY);
        setBlurAmt(0);
        setPhase("landing");
        audioRef.current?.landing();
        setFlash(true);
        scheduleTimeout(() => setFlash(false), 480);

        scheduleTimeout(() => {
          const baseRow = targetIdx;
          setTranslateY(-baseRow * ROW_H);
          setSelectedIndex(targetIdx);
          setPhase("active");
          runningRef.current = false;
        }, 620);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  /** Pulls the lever in an arc, then triggers the reel spin. */
  const pullLever = (targetIdx) => {
    if (runningRef.current || !items?.length) return;
    runningRef.current = true;
    setLeverPulled(true);
    // Reduced-motion: skip the lever animation but still fire the reel change
    if (reduced) {
      spinTo(targetIdx);
      scheduleTimeout(() => setLeverPulled(false), 100);
      return;
    }
    // Fire the "ka-CHUNK" when arm hits the bottom of its arc (~220ms in)
    scheduleTimeout(() => audioRef.current?.leverPull(), 220);
    // Reel begins spinning as the lever bottoms out
    scheduleTimeout(() => spinTo(targetIdx), 260);
    // Lever animation ends at 700ms; release the class so it can be re-triggered
    scheduleTimeout(() => setLeverPulled(false), 720);
  };

  /** Count consecutive user-initiated pulls. On 5 within a 4s window, open the secret modal. */
  const registerUserPull = () => {
    const now = Date.now();
    const gap = now - (hitRef.current.lastAt || 0);
    if (gap > 4000) hitRef.current.count = 0;
    hitRef.current.count += 1;
    hitRef.current.lastAt = now;
    if (hitRef.current.resetTimer) clearTimeout(hitRef.current.resetTimer);
    hitRef.current.resetTimer = setTimeout(() => {
      hitRef.current.count = 0;
      setHitDots(0);
    }, 4200);
    setHitDots(Math.min(5, hitRef.current.count));
    if (hitRef.current.count >= 5) {
      hitRef.current.count = 0;
      hitRef.current.lastAt = 0;
      clearTimeout(hitRef.current.resetTimer);
      setEasterBurst(true);
      // Small delay so users see the burst before the modal appears
      scheduleTimeout(() => window.dispatchEvent(new Event("cq:open-secret")), 520);
      scheduleTimeout(() => { setEasterBurst(false); setHitDots(0); }, 1200);
    }
  };

  const REST_ANGLE = -6;
  const MAX_ANGLE = 82;
  const COMMIT_THRESHOLD = 55;

  const onLeverPointerDown = (e) => {
    // Only handle primary pointer
    if (e.button && e.button !== 0) return;
    const el = e.currentTarget;
    try { el.setPointerCapture?.(e.pointerId); } catch {}
    // Always accept the pointer — even if the reel is spinning — so we can
    // count rapid taps toward the 5-in-a-row easter egg. When runningRef is
    // true, onPointerUp will register the hit but skip firing another spin.
    dragRef.current = { active: true, startY: e.clientY, moved: false, pointerId: e.pointerId };
    setDragEasing("none");
    // Only visually reset the arm to rest if we're not mid-animation
    if (!runningRef.current) setDragAngle(REST_ANGLE);
    setHasInteracted(true);
  };

  const onLeverPointerMove = (e) => {
    if (!dragRef.current.active) return;
    // Ignore drag movement while the reel is animating — the inline transform
    // would fight the keyframe. Taps still count toward the easter egg.
    if (runningRef.current) return;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dy) > 3) dragRef.current.moved = true;
    // 0.9 deg per px feels natural; clamp between rest and max
    let ang = REST_ANGLE + dy * 0.9;
    if (ang < REST_ANGLE) ang = REST_ANGLE;
    if (ang > MAX_ANGLE) ang = MAX_ANGLE;
    setDragAngle(ang);
  };

  const onLeverPointerUp = async (e) => {
    if (!dragRef.current.active) return;
    const el = e.currentTarget;
    try { el.releasePointerCapture?.(e.pointerId); } catch {}
    const moved = dragRef.current.moved;
    const ang = dragAngle ?? REST_ANGLE;
    dragRef.current.active = false;

    // Pure tap (no drag movement): reset drag state and fire the full keyframe pull
    if (!moved) {
      setDragAngle(null);
      setDragEasing("none");
      if (soundOn) await audioRef.current?.unlock();
      // Every user tap counts toward the 5-in-a-row easter egg, even if the reel
      // is still spinning from a previous pull (rapid taps must be counted).
      registerUserPull();
      if (runningRef.current) return;
      const nxt = (selectedIndex + 1) % items.length;
      pullLever(nxt);
      return;
    }

    if (reduced) {
      // Reduced-motion: just snap back and, if past threshold, trigger the reel change
      setDragAngle(null);
      if (ang >= COMMIT_THRESHOLD) {
        registerUserPull();
        if (!runningRef.current) {
          const nxt = (selectedIndex + 1) % items.length;
          pullLever(nxt);
        }
      }
      return;
    }

    if (ang >= COMMIT_THRESHOLD) {
      // Commit: finish the pull to the bottom. Register the hit even if the reel
      // is still spinning; only skip the reel change itself in that case.
      registerUserPull();
      const canSpin = !runningRef.current;
      if (canSpin) runningRef.current = true;
      setDragEasing("140ms cubic-bezier(0.4,0,0.2,1)");
      setDragAngle(MAX_ANGLE);
      if (soundOn) await audioRef.current?.unlock();
      audioRef.current?.leverPull();
      if (canSpin) {
        scheduleTimeout(() => {
          const nxt = (selectedIndex + 1) % items.length;
          spinTo(nxt);
        }, 40);
      }
      scheduleTimeout(() => {
        setDragEasing("520ms cubic-bezier(0.34,1.56,0.64,1)");
        setDragAngle(REST_ANGLE);
      }, 220);
      scheduleTimeout(() => {
        setDragAngle(null);
        setDragEasing("none");
      }, 780);
    } else {
      // Below threshold: spring back to rest
      setDragEasing("340ms cubic-bezier(0.34,1.56,0.64,1)");
      setDragAngle(REST_ANGLE);
      scheduleTimeout(() => {
        setDragAngle(null);
        setDragEasing("none");
      }, 380);
    }
  };

  // ---------- Visibility gate (IntersectionObserver + tab visibility) ----------
  // When the stat bar scrolls out of view (or the tab is hidden), we snap the
  // audio master gain to 0 so any in-flight spin clicks / lever thuds go silent
  // immediately, and we pause the auto-rotate loop so no *new* sounds get
  // scheduled either. Restores on return.
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio > 0.15),
      { threshold: [0, 0.15, 0.5, 1] }
    );
    io.observe(node);
    const onVis = () => {
      if (document.hidden) setVisible(false);
      else {
        // Re-evaluate: if the element is on screen we go back to true
        const rect = node.getBoundingClientRect();
        const inView = rect.top < window.innerHeight * 0.85 && rect.bottom > window.innerHeight * 0.15;
        setVisible(inView);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  useEffect(() => {
    // Mute or unmute the master gain immediately on visibility change
    audioRef.current?.setAudible(visible && soundOn);
  }, [visible, soundOn]);

  useEffect(() => {
    if (!items?.length) return;
    // Pause the auto-rotate loop entirely when off-screen — saves CPU AND
    // guarantees no new lever/spin sounds get scheduled.
    if (!visible) return;
    const loop = () => {
      const nxt = (selectedIndex + 1) % items.length;
      nextIdxRef.current = nxt;
      pullLever(nxt);
    };
    const id = setTimeout(loop, intervalMs);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [selectedIndex, items, intervalMs, visible]);

  const toggleSound = async () => {
    if (!soundOn) {
      await audioRef.current?.unlock();
      audioRef.current?.setEnabled(true);
      setSoundOn(true);
      window.localStorage.setItem("cq_slot_sound", "1");
    } else {
      audioRef.current?.setEnabled(false);
      setSoundOn(false);
      window.localStorage.setItem("cq_slot_sound", "0");
    }
  };

  if (!items?.length) return null;

  return (
    <div
      ref={rootRef}
      className="cq-slot-bar mt-6 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500 font-semibold"
      data-testid={testid}
    >
      <span className="inline-flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-600" /> Bank-level encryption</span>
      <span className="hidden sm:inline text-slate-300">·</span>
      <span>No credit card required</span>
      <span className="hidden sm:inline text-slate-300">·</span>

      {/* Slot machine chassis — window + lever */}
      <div className="cq-slot-machine relative flex items-stretch">
        <div
          className={`cq-slot-window ${phase === "spinning" ? "is-spinning" : ""} ${flash ? "is-flash" : ""}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="cq-slot-sweep" aria-hidden />
          <span className="cq-slot-vignette" aria-hidden />
          <div
            className="cq-slot-strip"
            style={{
              transform: `translate3d(0, ${translateY}px, 0)`,
              filter: blurAmt > 0.02 ? `blur(${blurAmt.toFixed(2)}px)` : "none",
              willChange: "transform, filter",
            }}
          >
            {strip.map((it) => (
              <div className="cq-slot-item" key={it.key}>
                {it.icon ? <it.icon size={13} className="text-amber-500 shrink-0" aria-hidden /> : null}
                <span className="truncate">{it.text}</span>
              </div>
            ))}
          </div>
          <span className="sr-only" data-testid="slot-current-text">{items[selectedIndex]?.text}</span>
        </div>

        {/* Physical lever */}
        <button
          type="button"
          className={`cq-lever ${leverPulled ? "is-pulled" : ""} ${hasInteracted ? "has-interacted" : ""} ${dragAngle !== null ? "is-dragging" : ""}`}
          onPointerDown={onLeverPointerDown}
          onPointerMove={onLeverPointerMove}
          onPointerUp={onLeverPointerUp}
          onPointerCancel={onLeverPointerUp}
          onLostPointerCapture={onLeverPointerUp}
          aria-label="Pull the lever — tap or drag down to spin the stat reel"
          title="Pull to spin"
          data-testid="slot-lever"
        >
          <span className="cq-lever-hint" aria-hidden>PULL</span>
          {/* Progress dots: 5 pulls in a row → unlock */}
          <span className={`cq-lever-dots ${hitDots > 0 ? "is-visible" : ""}`} aria-hidden data-testid="slot-lever-dots">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`cq-lever-dot ${i < hitDots ? "is-on" : ""}`} />
            ))}
          </span>
          <svg viewBox="0 0 40 96" width="34" height="82" aria-hidden focusable="false">
            <defs>
              <linearGradient id="cq-lever-chrome" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#94a3b8" />
                <stop offset="30%" stopColor="#f1f5f9" />
                <stop offset="55%" stopColor="#cbd5e1" />
                <stop offset="80%" stopColor="#e2e8f0" />
                <stop offset="100%" stopColor="#64748b" />
              </linearGradient>
              <linearGradient id="cq-lever-brass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fde68a" />
                <stop offset="60%" stopColor="#d97706" />
                <stop offset="100%" stopColor="#78350f" />
              </linearGradient>
              <radialGradient id="cq-lever-ball" cx="0.35" cy="0.3" r="0.7">
                <stop offset="0%" stopColor="#fee2e2" />
                <stop offset="30%" stopColor="#f87171" />
                <stop offset="75%" stopColor="#b91c1c" />
                <stop offset="100%" stopColor="#450a0a" />
              </radialGradient>
              <radialGradient id="cq-lever-mount" cx="0.4" cy="0.35" r="0.65">
                <stop offset="0%" stopColor="#fef3c7" />
                <stop offset="55%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#78350f" />
              </radialGradient>
            </defs>
            {/* Mount plate (brass) — pivot point */}
            <circle cx="20" cy="10" r="9" fill="url(#cq-lever-mount)" stroke="#78350f" strokeWidth="1" />
            <circle cx="20" cy="10" r="4" fill="#78350f" />
            <circle cx="18.5" cy="8.8" r="1.4" fill="#fef3c7" opacity="0.65" />

            {/* Arm + knob — rotates around (20, 10) */}
            <g
              className="cq-lever-arm"
              style={dragAngle !== null ? {
                transform: `rotate(${dragAngle}deg)`,
                transition: dragEasing !== "none" ? `transform ${dragEasing}` : "none",
              } : undefined}
            >
              {/* Arm shaft with chrome highlight */}
              <rect x="16.5" y="10" width="7" height="62" rx="3.2" fill="url(#cq-lever-chrome)" stroke="#334155" strokeWidth="0.6" />
              {/* Vertical specular highlight */}
              <rect x="18.2" y="12" width="1.6" height="58" rx="0.8" fill="#ffffff" opacity="0.75" />
              {/* Collar just above the knob */}
              <rect x="15.5" y="70" width="9" height="4" rx="1.4" fill="url(#cq-lever-brass)" stroke="#78350f" strokeWidth="0.5" />
              {/* Red knob */}
              <circle cx="20" cy="84" r="10" fill="url(#cq-lever-ball)" stroke="#450a0a" strokeWidth="0.7" />
              {/* Specular hotspot */}
              <ellipse cx="16.5" cy="80.5" rx="3.2" ry="2.2" fill="#ffffff" opacity="0.7" />
              {/* Under-shadow crescent */}
              <path d="M12 87 Q20 96 28 87" stroke="#450a0a" strokeWidth="0.4" fill="none" opacity="0.6" />
            </g>
          </svg>
          {/* Easter-egg burst overlay — fires on 5th consecutive pull */}
          {easterBurst && (
            <span className="cq-lever-burst" aria-hidden data-testid="lever-easter-burst">
              <span className="cq-lever-burst-flash" />
              {[...Array(18)].map((_, i) => {
                const angle = (i / 18) * Math.PI * 2;
                const dist = 60 + (i % 4) * 12;
                return (
                  <span
                    key={i}
                    className="cq-lever-spark"
                    style={{
                      // eslint-disable-next-line
                      "--dx": `${Math.cos(angle) * dist}px`,
                      "--dy": `${Math.sin(angle) * dist}px`,
                      animationDelay: `${(i % 6) * 0.02}s`,
                    }}
                  />
                );
              })}
            </span>
          )}
        </button>
      </div>

      <button
        type="button"
        onClick={toggleSound}
        aria-label={soundOn ? "Mute rotating stats sound" : "Unmute rotating stats sound"}
        title={soundOn ? "Sound on — click to mute" : "Sound off — click to enable"}
        className="cq-slot-mute ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
        data-testid="slot-sound-toggle"
      >
        {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
      </button>
    </div>
  );
}
